import React from 'react';

import Group from '@lightspeed/cirrus-group';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '@lightspeed/cirrus-modal';
import { Button } from '@lightspeed/flame/Button';
import { IconCheckmark } from '@lightspeed/flame/Icon/Checkmark';
import { Spinner } from '@lightspeed/flame/Spinner';
import PropTypes from 'prop-types';

import LabelPrintModal from '../LabelPrintModal';
import LabelPrintTracking from '../LabelPrintTracking';

import CheckAgain from './CheckAgain';
import { DeprecationNotice } from './DeprecationNotice';

/**
 * Class Installing displays the "Waiting for App to be installed" modal
 */
class Installing extends React.Component {
  /**
   * Construct the Installing Modal
   * @param {Object} props - Component properties
   */
  constructor(props) {
    super(props);

    this.state = {
      isInstalled: false,
      isCheckAgainVisible: false,
    };

    this.close = this.close.bind(this);
    this.handleCheckAgainClick = this.handleCheckAgainClick.bind(this);
    this.handleInstallingOnClick = this.handleInstallingOnClick.bind(this);
  }

  /**
   * After the App is opened via the "Installing" step, it will update
   * the modal and set a timeout to open the "Print" step
   * @param {Object} nextProps
   */
  componentWillReceiveProps(nextProps) {
    // Only update the state once
    if (nextProps.isInstalled && !this.state.isInstalled) {
      this.setState({
        isInstalled: nextProps.isInstalled,
      });
    }
  }

  /**
   * Implement additional functionality to the onHide/close parent function
   */
  close() {
    LabelPrintTracking.track(LabelPrintTracking.events.CLOSED_AFTER_DOWNLOADING);
    this.props.onHide();
  }

  /**
   * Toggle the visibility of the CheckAgain text
   */
  handleCheckAgainClick() {
    this.setState({
      isCheckAgainVisible: true,
    });
    LabelPrintTracking.track(LabelPrintTracking.events.VISITED_TROUBLESHOOT);
  }

  handleHelpOnClick() {
    LabelPrintTracking.track(LabelPrintTracking.events.VISITED_HELP, { step: 'Installing' });
  }

  handleInstallingOnClick() {
    if (this.state.isInstalled) {
      this.props.setStep(LabelPrintModal.steps.PRINT);
    } else {
      this.handleCheckAgainClick();
    }
  }

  /**
   * Render the Installing modal
   * @returns {Object} DOM Element
   */
  render() {
    const { isInstalled } = this.state;

    return (
      <Modal
        className={`label-print-modal ${this.props.currentStep.toLowerCase()} cirrus responsive`}
        isOpen={this.props.show}
        onRequestClose={this.close}
      >
        <ModalHeader onCloseClicked={this.close}>Finishing Installation</ModalHeader>
        <ModalBody>
          <div className="cr-pt-4 cr-mb-4">
            <DeprecationNotice callback={this.close} />
            <div className="cr-flex cr-width-full cr-items-center cr-mb-4">
              <div className="cr-inline-flex cr-mr-4">
                <img className="medal-zebra" src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-9919532/images/label/medal-zebra.svg" alt="zebra" />
              </div>
              <div>
                <h4 className="cr-text cr-bold cr-mb-0">Waiting for Zebra Browser Print</h4>
                <ol>
                  <li>Open the downloaded installer.</li>
                  <li>Install Zebra Browser Print.</li>
                  <li>Start Zebra Browser Print.</li>
                </ol>
                <p className="cr-text-s">
                  Is Zebra Browser Print already running?{' '}
                  <a href="" onClick={this.handleCheckAgainClick}>
                    Check Again
                  </a>
                </p>
              </div>
            </div>
            {this.state.isCheckAgainVisible && <CheckAgain />}
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="cr-flex cr-justify-between">
            <a href={LabelPrintModal.links.documentation} target="_blank" rel="noopener noreferrer">
              <Button variant="secondary" onClick={this.handleHelpOnClick}>
                Help
              </Button>
            </a>
            <Group className="cr-justify-end">
              <Button onClick={this.close}>Cancel</Button>
              <Button
                variant="primary"
                disabled={!isInstalled}
                onClick={this.handleInstallingOnClick}
              >
                {isInstalled ? <IconCheckmark /> : <Spinner />}
                Waiting for Zebra Browser Print
              </Button>
            </Group>
          </div>
        </ModalFooter>
      </Modal>
    );
  }
}

Installing.propTypes = {
  setStep: PropTypes.func.isRequired,
  currentStep: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default Installing;
